import { motion } from "framer-motion";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Award, Calendar, Building } from "lucide-react";
import { Link } from "wouter";

interface CertificateCardProps {
  certificate: {
    id: number;
    title: string;
    issuingOrganization: string;
    issueDate: string;
    expiryDate?: string | null;
    skills?: string[] | null;
    logoPath?: string | null;
    link?: string | null;
  };
  compact?: boolean;
  index?: number;
}

export default function CertificateCard({ certificate, compact = false, index = 0 }: CertificateCardProps) {
  const isExpired = certificate.expiryDate ? new Date(certificate.expiryDate) < new Date() : false;
  const isExpiringSoon = certificate.expiryDate ? 
    new Date(certificate.expiryDate) < new Date(Date.now() + 90 * 24 * 60 * 60 * 1000) && !isExpired : false;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'long'
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="h-full"
    >
      <Link to={`/certificate/${certificate.id}`} className="block h-full">
        <Card className={`
          h-full transition-all duration-300 hover:shadow-lg hover:scale-[1.02] 
          ${isExpired ? 'opacity-60' : ''} 
          ${compact ? 'p-3' : 'p-4'}
          group cursor-pointer border-2 hover:border-primary/20
        `}>
          <CardHeader className={compact ? 'pb-2' : 'pb-4'}>
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <CardTitle className={`
                  ${compact ? 'text-sm' : 'text-lg'} 
                  group-hover:text-primary transition-colors
                  line-clamp-2
                `}>
                  {certificate.title}
                </CardTitle>
                <div className="flex items-center gap-2 mt-2 text-muted-foreground">
                  <Building size={14} />
                  <span className={`${compact ? 'text-xs' : 'text-sm'}`}>
                    {certificate.issuingOrganization}
                  </span>
                </div>
              </div>
              
              {/* Certificate Logo */}
              {certificate.logoPath && (
                <div className="flex-shrink-0 ml-3">
                  <img 
                    src={certificate.logoPath} 
                    alt={`${certificate.issuingOrganization} Logo`}
                    className={`${compact ? 'h-8' : 'h-12'} w-auto object-contain`}
                  />
                </div>
              )}
            </div>
          </CardHeader>
          
          <CardContent className={`space-y-3 ${compact ? 'pt-0' : ''}`}>
            {/* Issue and Expiry Dates */}
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-1">
                <Calendar size={12} />
                <span>{formatDate(certificate.issueDate)}</span>
              </div>
              {certificate.expiryDate && (
                <div className={`flex items-center gap-1 ${
                  isExpired ? 'text-red-500' : isExpiringSoon ? 'text-orange-500' : ''
                }`}>
                  <span>•</span>
                  <span>Expires {formatDate(certificate.expiryDate)}</span>
                </div>
              )}
              {!certificate.expiryDate && (
                <div className="flex items-center gap-1 text-green-600">
                  <span>•</span>
                  <span>No Expiry</span>
                </div>
              )}
            </div>

            {/* Status Badge */}
            <div className="flex items-center gap-2">
              {isExpired && (
                <Badge variant="destructive" className="text-xs">
                  Expired
                </Badge>
              )}
              {isExpiringSoon && !isExpired && (
                <Badge variant="outline" className="text-xs border-orange-500 text-orange-600">
                  Expiring Soon
                </Badge>
              )}
              {!isExpired && !isExpiringSoon && (
                <Badge variant="default" className="text-xs bg-green-100 text-green-800 border-green-200">
                  Active
                </Badge>
              )}
            </div>

            {/* Skills */}
            {certificate.skills && certificate.skills.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {certificate.skills.slice(0, compact ? 3 : 5).map((skill, index) => (
                  <Badge 
                    key={index} 
                    variant="secondary" 
                    className={`${compact ? 'text-xs px-2 py-0.5' : 'text-xs'}`}
                  >
                    {skill}
                  </Badge>
                ))}
                {certificate.skills.length > (compact ? 3 : 5) && (
                  <Badge variant="outline" className="text-xs">
                    +{certificate.skills.length - (compact ? 3 : 5)}
                  </Badge>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </Link>
    </motion.div>
  );
}
